import React, { useState } from 'react';
import {
  Drawer, List, ListItem, ListItemButton, ListItemText,
  Button, Box, Toolbar, Divider,
  IconButton, Menu, MenuItem,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import AnimatedTitle from '../common/animatedTitle';
import SearchModal from '../common/searchModal';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../../context/chatContext';
import AdBanner from '../AdBanner';

const drawerWidth = 240;

interface SidebarProps {
  mobileOpen: boolean;
  onDrawerToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onDrawerToggle }) => {
  const {
    chats,
    activeChat,
    setActiveChatId,
    createNewChat,
    toggleSettingsModal,
    pinChat,
    deleteChat,
    pinnedChats,
    usageInfo,
  } = useChat();

  const navigate = useNavigate();

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuChatId, setMenuChatId] = useState<string | null>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const openMenu = (event: React.MouseEvent<HTMLElement>, chatId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuChatId(chatId);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuChatId(null);
  };

  const handlePin = () => {
    if (!menuChatId) return;
    pinChat(menuChatId);
    closeMenu();
  };

  const handleDelete = () => {
    if (!menuChatId) return;
    deleteChat(menuChatId);
    closeMenu();
  };
  
  const handleNewChat = () => {
    const id = createNewChat();
    setActiveChatId(id);
    navigate(`/chat/${id}`);
  };

  const pinnedSet = new Set(pinnedChats);
  const permanentChatId = pinnedChats[0];
  const permanentChat = chats.find((chat: { id: any }) => chat.id === permanentChatId);
  const otherPinned = chats.filter((chat: { id: any }) => pinnedSet.has(chat.id) && chat.id !== permanentChatId);
  const regularChats = chats.filter((chat: { id: any }) => !pinnedSet.has(chat.id));

 const drawerContent = (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'space-between',
    }}
  >
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <Toolbar />
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleNewChat}
          fullWidth
        >
          New Chat
        </Button>
      </Box>
      <Divider sx={{ my: 1 }} />
      <List sx={{ overflowY: 'auto', flexGrow: 1, p: 0 }}>
        {permanentChat && (
          <ListItem
            key={permanentChat.id}
            disablePadding
            sx={{ backgroundColor: '#545656' }}
          >
            <ListItemButton
              selected={activeChat?.id === permanentChat.id}
              onClick={() => navigate(`/chat/${permanentChat.id}`)}
            >
              <ListItemText
                primary={
                  <AnimatedTitle
                    text={permanentChat.title}
                    isActive={activeChat?.id === permanentChat.id}
                  />
                }
                primaryTypographyProps={{
                  noWrap: true,
                  style: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontWeight: 'bold',
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        )}

        {otherPinned.length > 0 && <Divider sx={{ my: 1 }} />}

        {otherPinned.map((chat: any) => (
          <ListItem
            key={chat.id}
            disablePadding
            secondaryAction={
              <IconButton
                edge="end"
                aria-label="more options"
                onClick={(e) => openMenu(e, chat.id)}
              >
                <MoreHorizIcon />
              </IconButton>
            }
          >
            <ListItemButton
              selected={activeChat?.id === chat.id}
              onClick={() => navigate(`/chat/${chat.id}`)}
            >
              <ListItemText
                primary={
                  <AnimatedTitle
                    text={chat.title}
                    isActive={activeChat?.id === chat.id}
                  />
                }
                primaryTypographyProps={{
                  noWrap: true,
                  style: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontWeight: 'bold',
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}

        {regularChats.length > 0 && <Divider sx={{ my: 1 }} />}

        {regularChats.map((chat: any) => (
          <ListItem
            key={chat.id}
            disablePadding
            secondaryAction={
              <IconButton
                edge="end"
                aria-label="more options"
                onClick={(e) => openMenu(e, chat.id)}
              >
                <MoreHorizIcon />
              </IconButton>
            }
          >
            <ListItemButton
              selected={activeChat?.id === chat.id}
              onClick={() => navigate(`/chat/${chat.id}`)}
            >
              <ListItemText
                primary={
                  <AnimatedTitle
                    text={chat.title}
                    isActive={activeChat?.id === chat.id}
                  />
                }
                primaryTypographyProps={{
                  noWrap: true,
                  style: { overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>

    <Box
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Box>
        <Divider sx={{ mb: 2 }} />
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={() => setIsSearchOpen(true)}
          fullWidth
        >
          Search Chats
        </Button>
        <Divider sx={{ my: 1 }} />
        <Button
          variant="contained"
          onClick={() => toggleSettingsModal(true, 'account')}
          fullWidth
        >
          Manage Account
        </Button>
      </Box>
      {usageInfo?.tier === 'Free' && usageInfo?.showAds && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <AdBanner slot="3704036424102976" />
        </Box>
      )}
    </Box>
  </Box>
);

  return (
    <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
      <SearchModal open={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            boxShadow: 'none',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            boxShadow: 'none',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 160 } } }}
      >
        <MenuItem onClick={handlePin}>
          <PushPinIcon fontSize="small" style={{ marginRight: 8 }} />
          {menuChatId && pinnedSet.has(menuChatId) ? 'Unpin' : 'Pin'}
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Sidebar;